import { Injectable, effect, inject } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { catchError, lastValueFrom, Observable, of, switchMap, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthUtils } from 'app/_helper/auth/auth.utils';
import { UserService } from 'app/_services/user.service';
import { environment } from 'environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { cloneDeep } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _authenticated: boolean = false;
  private _user: any;
  private _httpClient = inject(HttpClient);
  private _userService = inject(UserService);

  CookieService = inject(CookieService);

  private _httpCLinetWithoutInterceptor;
  /**
   * Constructor
   */
  constructor(handler: HttpBackend) {
    this._httpCLinetWithoutInterceptor = new HttpClient(handler);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for access token
   */
  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
    var expireDT = new Date();
    var time = expireDT.getTime();
    var expireTime = time + 1000 * 36000;
    expireDT.setTime(expireTime);
    var domainName = window.location.hostname;
    domainName = domainName.replace(/^[^.]+\./g, '');
    document.cookie = 'wmt=' + token + ';expires=' + expireDT.toUTCString() + ';path=/ secure; domain=.' + domainName;
    //this.CookieService.set('wmt', token);
  }

  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  set userData(data: any) {
    localStorage.setItem('userData', JSON.stringify(data));
  }
  get userData() {
    return JSON.parse(localStorage.getItem('userData'));
  }
  get isProduction(): boolean {
    return environment.production;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   *
   * @param email
   */
  async forgotPassword(email: string): Promise<any> {
    try {
      return await lastValueFrom(
        this._httpClient.post(
          `${environment.BASEURLAPI + environment.FORGOTPASSWORD}`,
          {
            emailAddress: email,
          },
          {
            responseType: 'text',
            headers: {
              'X-Origin-Page': window.location.pathname, // e.g., "/wmportal/forgot-password"
            },
          },
        ),
      );
      //try {
      //  return await lastValueFrom(
      //    this._httpClient.post(
      //      'https://localhost:44375/api/account/forgotpassword',
      //      { emailAddress: email },
      //      {
      //        responseType: 'text',
      //        headers: {
      //          'X-Origin-Page': window.location.pathname  // e.g., "/wmportal/forgot-password"
      //        }
      //      }
      //    )
      //  );
    } catch (error: any) {
      let errorMessage = 'Something went wrong. Please try again later.';

      try {
        const parsedError = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;

        if (error.status === 404) {
          errorMessage = 'Email address not recognised';
        } else if (error.status === 400 && parsedError?.errors) {
          const validationErrors = Object.values(parsedError.errors).flat();
          errorMessage = validationErrors.join('\n');
        } else if (parsedError?.message) {
          errorMessage = parsedError.message;
        }
      } catch {
        if (typeof error.error === 'string') {
          errorMessage = error.error;
        }
      }

      throw new Error(errorMessage);
    }
  }

  async forgotMobileUserPassword(obj): Promise<any> {
    return await lastValueFrom(this._httpClient.post(`${environment.BASEURLAPI + environment.FORGOTMOBILEUSERPASSWORD}`, obj));
  }
  async changePassword(form: any): Promise<any> {
    const credentials = {
      Username: form.controls.email.value,
      CurrentPassword: form.controls.password.value,
      NewPassword: form.controls.newPassword.value,
      ConfirmPassword: form.controls.confirmPassword.value,
    };

    try {
      return await lastValueFrom(this._httpClient.post(`${environment.BASEURLAPI}${environment.CHANGEPASSWORDURL}`, credentials, { responseType: 'text' }));
    } catch (error: any) {
      let errorMessage = 'An error occurred while changing the password.';

      try {
        // If error.error is a JSON string, parse it
        const parsedError = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;

        if (error.status === 400 && parsedError?.errors) {
          const validationErrors = Object.values(parsedError.errors).flat();
          errorMessage = validationErrors.join('\n');
        } else if (parsedError?.message) {
          errorMessage = parsedError.message;
        }
      } catch {
        // Fallback if parsing fails
        if (typeof error.error === 'string') {
          errorMessage = error.error;
        }
      }

      throw new Error(errorMessage);
    }
  }

  /**
   * Reset password
   *
   * @param password
   */
  async resetPassword(tempHash: string, newPassword: string, confirmPassword: string): Promise<any> {
    const obj = {
      TemporaryHash: tempHash,
      NewPassword: newPassword,
      ConfirmPassword: confirmPassword,
    };

    try {
      return await lastValueFrom(this._httpClient.post(`${environment.BASEURLAPI + environment.RESETPASSWORD}?temporaryHash=${tempHash}&Password=${newPassword}`, obj));
    } catch (error: any) {
      let errorMessage = 'Something went wrong. Please try again later.';

      try {
        const parsedError = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;

        if (error.status === 404) {
          errorMessage = 'Reset link is invalid or expired.';
        } else if (error.status === 400 && parsedError?.errors) {
          const validationErrors = Object.values(parsedError.errors).flat();
          errorMessage = validationErrors.join('\n');
        } else if (parsedError?.message) {
          errorMessage = parsedError.message;
        }
      } catch {
        if (typeof error.error === 'string') {
          errorMessage = error.error;
        }
      }

      throw new Error(errorMessage);
    }
  }

  async resetMobileUserPassword(tempHash: string, password: string): Promise<any> {
    let obj = { temporaryHash: tempHash, Password: password };
    return await lastValueFrom(this._httpClient.post(`${environment.BASEURLAPI + environment.RESETMOBILEUSERPASSWORD}?temporaryHash=${tempHash}&Password=${password}`, obj));
  }

  /**
   * Sign in
   *
   * @param credentials
   */

  async signIn(credentials: { email: string; password: string }): Promise<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError('User is already logged in.');
    }
    return await lastValueFrom(this._httpClient.post(environment.BASEURLAPI + environment.AUTHENTICATIONURL, credentials, { responseType: 'text' })).then(async (response: any) => {
      // Store the access token in the local storage
      const tokenData = this.getDecodedAccessToken(response);
      let logInCheck = JSON.parse(tokenData.Roles).filter((data) => data.Name == 'CanLoginWMPortal');
      if (logInCheck.length == 0) throw { status: 401 };
      this.accessToken = response;
      // Set the authenticated flag to true
      this._authenticated = true;
      cloneDeep(this._user);
      // Store the user on the user service
      this.userData = {
        email: credentials.email,
        changePasswordRequired: tokenData.Security ? true : false,
      };
      // Return a new observable with the response
      return this.userData;
    });
  }

  getDecodedAccessToken(token: string): any {
    try {
      return AuthUtils.getTokenData(token);
    } catch (Error) {
      return null;
    }
  }

  getDecodedAccessRoles(): any {
    try {
      return JSON.parse(AuthUtils.getTokenData(this.accessToken).Roles);
    } catch (Error) {
      return null;
    }
  }

  /**
   * Get isCRMEnabled from token
   * Returns true if CRM is enabled for the current user, false otherwise
   */
  isCRMEnabled(): boolean {
    try {
      const tokenData = AuthUtils.getTokenData(this.accessToken);
      return String(tokenData?.IsCRMEnabled).toLowerCase() === 'true';
    } catch (Error) {
      return false;
    }
  }

  /**
   * Get isRoutingEnabled from token
   * Returns true if Job Routing is enabled for the current user, false otherwise
   */
  isRoutingEnabled(): boolean {
    try {
      const tokenData = AuthUtils.getTokenData(this.accessToken);
      return tokenData?.IsRoutingEnabled === 'True';
    } catch (Error) {
      return false;
    }
  }

  isLoneWorkerEnabled(): boolean {
    try {
      const tokenData = AuthUtils.getTokenData(this.accessToken);
      return tokenData?.IsLoneWorkerEnabled === 'True';
    } catch (Error) {
      return false;
    }
  }

  

  validateUser(credentials: { email: string; password: string }) {
    return this._httpCLinetWithoutInterceptor.post(environment.BASEURLAPI + environment.AUTHENTICATIONURL, credentials, { responseType: 'text' });
  }
  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<any> {
    // Sign in using the token
    return this._httpClient
      .post('api/auth/sign-in-with-token', {
        accessToken: this.accessToken,
      })
      .pipe(
        catchError(() =>
          // Return false
          of(false),
        ),
        switchMap((response: any) => {
          // Replace the access token with the new one if it's available on
          // the response object.
          //
          // This is an added optional step for better security. Once you sign
          // in using the token, you should generate a new one on the server
          // side and attach it to the response object. Then the following
          // piece of code can replace the token with the refreshed one.
          if (response.accessToken) {
            this.accessToken = response.accessToken;
          }

          // Store the user on the user service
          this._userService.user$ = response.user;

          // Return true
          return of(true);
        }),
      );
  }

  /**
   * Sign out
   */
  signOut(): Observable<any> {
    // Remove the access token from the local storage
    localStorage.removeItem('accessToken');

    // Return the observable
    return of(true);
  }

  /**
   * Sign up
   *
   * @param user
   */
  signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
    return this._httpClient.post('api/auth/sign-up', user);
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: { email: string; password: string }): Observable<any> {
    return this._httpClient.post('api/auth/unlock-session', credentials);
  }

  /**
   * Check the authentication status
   */
  check(): boolean {
    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.accessToken) || !this.accessToken) {
      return false;
    }

    return true;
    // // Check the access token availability
    // else if (!this.accessToken) {
    //   return true;
    // }

    // If the access token exists and it didn't expire, sign in using it
    // return this.signInUsingToken();
  }

  authenticateUser(token, email) {
    // console.log('authenticateUser', token, email);
    try {
      if (!AuthUtils.isTokenExpired(token)) {
        const tokenData = this.getDecodedAccessToken(token);

        var lastLogin = JSON.parse(localStorage.getItem('userData') || '{}');

        if (!lastLogin || lastLogin.email != email) {
          localStorage.clear();
        }

        // Store the user on the user service
        localStorage.setItem(
          'userData',
          JSON.stringify({
            email: email,
            id: tokenData.CustomerId,
            changePasswordRequired: tokenData.Security ? true : false,
          }),
        );

        this.accessToken = token;
        return '';
      } else {
        return 'Token is Expired';
      }
    } catch (error) {
      return 'error';
    }
  }
  async verifyAccount(tempHash: string) {
    // VerifyRegistration
    return await lastValueFrom(this._httpClient.get(`${environment.BASEURLAPI}${environment.ACCOUNTVERIFICATION}?hash=${tempHash}`));
  }
}
